/**
 * Copyright 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { noop } from "@tanstack/react-table";
import styled from "styled-components";
import { Checkbox, FlexContainer, theme, Text } from "@nordcloud/gnui";
import { SidebarItemWrapper } from "~/components";
import { SwitchWrap } from "../AppEnvSelector/styles";
import { ApplicationOption, EnvironmentOption } from "../types";

type Props = {
  application: ApplicationOption;
  selectedEnvs: EnvironmentOption[];
  isAppChecked: boolean;
  disabled: boolean;
  onEnvCheck: (env: EnvironmentOption) => void;
};

export function EnvironmentsList({
  application,
  selectedEnvs,
  isAppChecked,
  disabled,
  onEnvCheck,
}: Props) {
  return (
    <>
      {application.environments.map((env) => (
        <EnvironmentRow
          key={env.nid}
          environment={env}
          selectedEnvs={selectedEnvs}
          isAppChecked={isAppChecked}
          disabled={disabled}
          onEnvCheck={onEnvCheck}
        />
      ))}
    </>
  );
}

type EnvRowProps = {
  environment: EnvironmentOption;
  selectedEnvs: EnvironmentOption[];
  isAppChecked: boolean;
  disabled: boolean;
  onEnvCheck: (env: EnvironmentOption) => void;
};

function EnvironmentRow({
  environment,
  selectedEnvs,
  isAppChecked,
  disabled,
  onEnvCheck,
}: EnvRowProps) {
  const { nid, name } = environment;
  const isEnvChecked =
    isAppChecked ||
    selectedEnvs.some(({ nid: selectedNid }) => selectedNid === nid);

  return (
    <StyledFlexContainer key={nid}>
      <SidebarItemWrapper
        hideBottomBorder
        title={name}
        data-testid="sidebar-item"
        onClick={noop}
      >
        <SwitchWrap>
          <Checkbox
            withoutLabel
            data-testid="env-filter-checkbox"
            disabled={disabled}
            checked={isEnvChecked}
            onChange={() => {
              onEnvCheck(environment);
            }}
          />
        </SwitchWrap>
        <Text mb="0">{name}</Text>
      </SidebarItemWrapper>
    </StyledFlexContainer>
  );
}

const StyledFlexContainer = styled(FlexContainer)`
  column-gap: ${theme.spacing.spacing04};
  padding-left: calc(${theme.spacing.spacing08} + 0.5rem);
  background-color: ${theme.color.background.ui03};
`;
